import * as Yup from 'yup';


// User Validation.
const validateMobilePrefix = Yup.number()
    .required('Mobile prefix required')
    .min(4, 'Invalid prefix');

const validateMobile = Yup.string()
    .required('Mobile number is required')
    .matches(/^\d{10}$/, 'Mobile number must be exactly 10 digits');

const validatePhone = Yup.string()
    .nullable()
    .matches(/^\d{10}$/, 'Phone number must be exactly 10 digits');

const validateAllocatedSpace = Yup.number()
    .required('Allocated space is required.')
    .positive('Allocated space must be a positive number in GB')
    .min(0.1, 'Allocated space must be at least 0.1 GB');

const validateAccessRestrictionMasterId = Yup.number()
    .test(
        'required-if-checked',
        'Please select access option',
        function () {
            const { enable_access_restriction, access_restriction_master_id } = this.parent;
            return (enable_access_restriction && access_restriction_master_id === null)
                ? this.createError({
                    message: 'If you enable access restriction, need to select Access restriction master',
                })
                : true;
        }
    )
    .nullable();

// User Edit Form.
export const userEditFormValidation = Yup.object().shape({
    phone: validatePhone,
    mobile: validateMobile,
    mobile_prefix: validateMobilePrefix,
    allocated_space: validateAllocatedSpace,
    last_name: Yup.string().required('Last Name is required'),
    first_name: Yup.string().required('First Name is required'),
    email_other: Yup.string().email('Invalid email address').nullable(),
    enable_access_restriction: Yup.boolean(),
    access_restriction_master_id: validateAccessRestrictionMasterId
});

// User Export Form.
export const userExportFormValidation = Yup.object().shape({
    domain_id: Yup.array()
        .min(1, 'Please select at least one domain')
        .required('Required to select at least one domain'),
    file_types: Yup.number()
        .required('Please select at least one file type to download'),
});

// Password Change Form.
const validatePassword = Yup.string()
    .trim()
    .min(8, 'Password must be at least 8 characters')
    .matches(/[A-Z]/, 'Password must contain at least one uppercase letter (A-Z)')
    .matches(/[a-z]/, 'Password must contain at least one lowercase letter (a-z)')
    .matches(/\d/, 'Password must contain at least one number (0-9)')
    .matches(/[@$!%*?&]/, 'Password must contain at least one special character (@$!%*?&)')
    .required('Password is required');

const validateConfirmPassword = Yup.string()
    .trim()
    .oneOf([Yup.ref('password'), null], 'Password must match')
    .required('Confirm password is required');

export const userPasswordChangeFormValidation = Yup.object().shape({
    password: validatePassword,
    confirmPassword: validateConfirmPassword,
});

// User Create Form
const validateLocalPart = Yup.string()
    .required('local_part required.')
    .trim()
    .min(2, 'local_part must be at least 2 characters')
    .max(198, 'local_part must be at most 198 characters')
    .matches(/^[a-z0-9._-]+$/, 'local_part must contain only alphanumeric characters with (.) dots, (-) hyphens, and (_) underscores');

export const userCreateFormValidation = Yup.object().shape({
    first_name: Yup.string().required('First name is required'),
    last_name: Yup.string().required('Last name is required'),
    mobile_prefix: validateMobilePrefix,
    mobile: validateMobile,
    local_part: validateLocalPart,
    active: Yup.boolean().required('Active is required'),
    domain_id: Yup.string().required('Domain id is required'),
    allocated_space: Yup.number()
        .required('Allocated space is required.')
        .positive('Allocated space must be a positive number in GB')
        .min(0.1, 'Allocated space must be at least 0.1 GB'),
    password: validatePassword,
});

const validateDomainId = Yup.number()
    .required('domain_id is required.')
    .test(
        'check domain-id',
        'Invalid domain_id. It should match given domain_id',
        function (value) {
            const domainDetails = JSON.parse(sessionStorage.getItem('domainDetails'));
            return domainDetails.hasOwnProperty(value);
        }
    );

const validateDomainName = Yup.string().required('domain_name is required.').trim()
    .test('domain-name-matches', 'Domain Name does not match the given Domain ID',
        function (value) {
            const domainDetails = JSON.parse(sessionStorage.getItem('domainDetails'));
            const { domain_id } = this.parent;
            if (!domainDetails.hasOwnProperty(domain_id)) {
                return this.createError({ path: 'domain_id', message: 'Invalid Domain ID' });
            }
            if (domainDetails[domain_id] !== value) {
                return this.createError({ path: 'domain_name', message: 'Domain Name does not match the given Domain ID' });
            }
            return true;
        }
    );

const emptyStringToNull = (value, originalValue) => {
    if (originalValue === "" || originalValue === null) {
        return null;
    }

    const num = Number(originalValue);
    return isNaN(num) ? null : num;
}

const validateIdsNullable =Yup.number()
    .transform(emptyStringToNull)
    .nullable();

// User Edit Import Form
export const userEditImportFormValidataion = Yup.object().shape({
    id: Yup.number().required('id required.').positive('Id must be positive')
        .integer('Id must be integer.'),
    active: Yup.boolean().required('user active is required.'),
    first_name: Yup.string().required('First name required'),
    last_name: Yup.string().required('Last name required'),
    mobile: Yup.string().required('Mobile number required.')
        .matches(/^\d{10}$/, 'Mobile number must be exactly 10 digits'),
    mobile_prefix: Yup.number().required('Mobile prefix required').min(4, 'Invalid prefix'),
    local_part: Yup.string().required('local_part required.'),
    two_fa_enabled: Yup.boolean().required('two_fa_enabled required.'),
    mailarchive: Yup.boolean().required('mailarchive is required.'),
    is_hybrid: Yup.boolean().required('is_hybrid is required.'),
    spam_filter_bypass: Yup.boolean().required('spam_filter_bypass is required.'),
    enable_access_restriction: Yup.boolean().required('enable_access_restriction is required.'),
    disable_password_expiration: Yup.boolean().required('disable_password_expiration is required.'),
    is_domain_admin: Yup.boolean().required('is_domain_admin is required.'),
    domain_id: validateDomainId,
    domain_name: validateDomainName,
    password: Yup.string()
        .trim()
        .min(8, 'Password must be at least 8 characters')
        .matches(/[A-Z]/, 'Password must contain at least one uppercase letter (A-Z)')
        .matches(/[a-z]/, 'Password must contain at least one lowercase letter (a-z)')
        .matches(/\d/, 'Password must contain at least one number (0-9)')
        .matches(/[@$!%*?&]/, 'Password must contain at least one special character (@$!%*?&)')
        .nullable(),
    employee_id: validateIdsNullable,
    phone: Yup.string()
        .matches(/^\d{10}$/, 'Phone number must be exactly 10 digits')
        .nullable(),
    designation: Yup.string().nullable(),
    allocated_space: Yup.number()
        .positive('used_quota must be positive')
        .min(0.1, 'Allocated space must be at least 0.1 GB')
        .nullable(),
    policy_id: validateIdsNullable,
    access_restriction_master_id: validateIdsNullable,
    address: Yup.string().nullable(),
    email_other: Yup.string().email('Invalid email address').nullable(),
    incoming_connector_id: validateIdsNullable,
    outgoing_connector_id: validateIdsNullable,
    department_id: validateIdsNullable,
    country_info: Yup.array().of(Yup.number()).nullable(),
});



// User Create Import Form
export const userCreateImportFormValidataion = Yup.object().shape({
    active: Yup.boolean().required('user active is required.'),
    first_name: Yup.string().required('First name required'),
    last_name: Yup.string().required('Last name required'),
    mobile: validateMobile,
    mobile_prefix: validateMobilePrefix,
    local_part: validateLocalPart,
    password: validatePassword,
    allocated_space: Yup.number()
        .positive('allocated_space must be positive')
        .required('Allocated space is required.')
        .min(0.1, 'Allocated space must be at least 0.1 GB'),
    domain_id: validateDomainId,
    domain_name: validateDomainName,
    // new added not required but need to validate type
    two_fa_enabled: Yup.boolean().required('two_fa_enabled required.'),
    mailarchive: Yup.boolean().required('mailarchive is required.'),
    is_hybrid: Yup.boolean().required('is_hybrid is required.'),
    spam_filter_bypass: Yup.boolean().required('spam_filter_bypass is required)'),
    enable_access_restriction: Yup.boolean().required('enable_access_restriction is required.'),
    disable_password_expiration: Yup.boolean().required('disable_password_expiration is required.'),
    is_domain_admin: Yup.boolean().required('is_domain_admin is required.'),
    employee_id: Yup.string().nullable(),
    phone: Yup.string()
        .nullable().matches(/^\d{10}$/, 'Phone number must be exactly 10 digits'),
    designation: Yup.string().nullable(),
    policy_id: validateIdsNullable,
    access_restriction_master_id: validateIdsNullable,
    address: Yup.string().nullable(),
    email_other: Yup.string().email('Invalid email address').nullable(),
    incoming_connector_id: validateIdsNullable,
    outgoing_connector_id: validateIdsNullable,
    department_id: validateIdsNullable,
    country_info: Yup.array().of(Yup.number()).nullable(),
});